import Config from '@config';
import { normalizeInfiniteQueryData } from '@gasbi/shared/common/features/utils';
import { createApi } from '@reduxjs/toolkit/query/react';

import { ReferenceBook } from '@/features/organizations/types';
import { FiltersGrid, Pagination } from '@/features/types';
import { infiniteQueryOptions } from '@/features/utils';
import { ReportFormType } from '@/forms/ReportForm/constants';
import { ReportGenerationStatus } from '@/pages/ReportHistory/components/ReportGenerationStatusCell';
import axiosBaseQuery from '@/utils/axiosBaseQuery';

import {
  AllowedReport,
  AnalyticsMeteringPointParams,
  BalancePreviewResponse,
  CapacityPreviewResponse,
  ExpiredDevice,
  ExpiredDeviceParams,
  ExportOperationalExpensesRequestBody,
  ExportReportFileType,
  GasDistributionByCategoryChartFilters,
  GenericPreviewRequestParams,
  MeteringPointToDisconnect,
  MeteringPointToVerify,
} from './types';
import {
  ExportMutationParams,
  GenericEntityRequest,
  GenericReportEntityResponse,
  GenericReportModelResponse,
} from './types';

const transformAppendix2ExportKeyResponse = response => {
  const id = JSON.parse(response);
  return {
    link: `${Config.VITE_ANALYTICS_API_URL}/reports/counterparty-contract-appendix2/export/${id}`,
  };
};

const transformResponse = (response, meta) => ({
  filename: decodeURI(
    meta.get('content-disposition').match(/=UTF-8''(.+)$/)[1],
  ),
  data: response,
});

const tagTypes = [
  'Reports',
  'Info',
  'Preview',
  'ExpiredDevices',
  'MeteringPointsToDisconnect',
  'MeteringPointsToVerify',
  'GasDistribution',
  'AllowedReports',
];

const genericPaginationOptions = {
  initialPageParam: { page: { number: 1, size: 50 } },
  getNextPageParam: (lastPage, _, lastPageParam) => {
    if (
      !lastPage?.rows?.length ||
      lastPage?.rows?.length < lastPageParam.page.size
    ) {
      return;
    }
    return {
      page: {
        number: lastPageParam.page.number + 1,
        size: lastPageParam.page.size,
      },
    };
  },
};

export const reportsApi = createApi({
  reducerPath: 'reportsApi',
  baseQuery: axiosBaseQuery({
    baseUrl: `${Config.VITE_ANALYTICS_API_URL}/reports`,
  }),
  tagTypes,
  endpoints: builder => ({
    exportOperationalExpenses: builder.mutation<
      string,
      ExportOperationalExpensesRequestBody
    >({
      query: data => ({
        url: `/form2/export`,
        method: 'POST',
        data,
      }),
    }),
    getFormHistory: builder.query<
      {
        id: string;
        organizationId: string;
        date: string;
        year: number;
        month: number;
        generationDate: string;
        status: ReportGenerationStatus;
        sourceType: ReferenceBook;
      }[],
      { formType: ReportFormType }
    >({
      query: ({ formType }) => ({
        url: `/${formType}/query`,
      }),
      providesTags: () => ['Reports'],
    }),
    getFormHistoryI: builder.infiniteQuery<
      {
        id: string;
        organizationId: string;
        date: string;
        year: number;
        month: number;
        generationDate: string;
        status: ReportGenerationStatus;
        sourceType: ReferenceBook;
      }[],
      { formType: ReportFormType },
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg: { formType, ...args }, pageParam }) => ({
        url: `/${formType}/query`,
        params: { ...pageParam, ...args },
      }),
      providesTags: () => ['Reports'],
    }),
    getPreview: builder.query<
      BalancePreviewResponse | CapacityPreviewResponse,
      { formType: ReportFormType; reportInstanceId: string }
    >({
      query: ({ formType, reportInstanceId }) => ({
        url: `/${formType}/${reportInstanceId}`,
        method: 'GET',
      }),
      providesTags: () => ['Preview'],
      keepUnusedDataFor: 0,
    }),
    getPreviewI: builder.infiniteQuery<
      BalancePreviewResponse | CapacityPreviewResponse,
      { formType: ReportFormType; reportInstanceId: string },
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: `/${queryArg.formType}/${queryArg.reportInstanceId}`,
        params: { ...pageParam },
      }),
      providesTags: () => ['Preview'],
      keepUnusedDataFor: 0,
    }),
    exportForm: builder.query<
      { filename: string; data: string },
      {
        formType: ReportFormType;
        reportInstanceId: string;
        exportType: ExportReportFileType;
        asBlob?: boolean;
      }
    >({
      query: ({ formType, reportInstanceId, exportType, asBlob = false }) => ({
        url: `/${formType}/${reportInstanceId}/export/${exportType}`,
        method: 'POST',
        transformResponse,
        responseType: asBlob ? 'blob' : 'json',
      }),
    }),
    generateForm: builder.mutation<
      string,
      {
        formType: ReportFormType;
        startDate?: string;
        endDate?: string;
        sourceType?: string;
        date?: string;
        year?: number;
        month?: number;
        startYear?: number;
        startMonth?: number;
        endYear?: number;
        endMonth?: number;
      }
    >({
      query: ({ formType, ...data }) => ({
        url: `/${formType}/generate`,
        method: 'POST',
        data,
      }),
      invalidatesTags: () => ['Reports', 'Info'],
    }),
    regenerateForm: builder.mutation<
      string,
      { formType: ReportFormType; reportInstanceId: string }
    >({
      query: ({ formType, reportInstanceId }) => ({
        url: `/${formType}/${reportInstanceId}/regenerate`,
        method: 'POST',
      }),
      invalidatesTags: () => ['Reports', 'Info'],
    }),
    removeForm: builder.mutation<
      string,
      { formType: ReportFormType; reportInstanceId: string }
    >({
      query: ({ formType, reportInstanceId }) => ({
        url: `/${formType}/${reportInstanceId}`,
        method: 'DELETE',
      }),
      invalidatesTags: () => ['Reports', 'Info'],
    }),
    reportInfo: builder.query<any, { formType: ReportFormType }>({
      query: ({ formType }) => ({
        url: `/${formType}/info`,
      }),
      providesTags: ['Info'],
    }),
    getExpiredDevices: builder.query<ExpiredDevice[], ExpiredDeviceParams>({
      query: params => ({
        url: `/expired-devices/query`,
        params,
      }),
      providesTags: ['ExpiredDevices'],
      keepUnusedDataFor: 0,
    }),
    getExpiredDevicesI: builder.infiniteQuery<
      ExpiredDevice[],
      ExpiredDeviceParams,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: '/expired-devices/query',
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: () => ['ExpiredDevices', 'ExpiredDevice'],
    }),
    getMeteringPointsToDisconnect: builder.query<
      MeteringPointToDisconnect[],
      AnalyticsMeteringPointParams
    >({
      query: params => ({
        url: `/metering-points-to-disconnect/query`,
        params,
      }),
      providesTags: ['MeteringPointsToDisconnect'],
      keepUnusedDataFor: 0,
    }),
    getMeteringPointsToDisconnectI: builder.infiniteQuery<
      MeteringPointToDisconnect[],
      AnalyticsMeteringPointParams,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: `/metering-points-to-disconnect/query`,
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: () => ['MeteringPointsToDisconnect'],
    }),
    getMeteringPointsToVerify: builder.query<
      MeteringPointToVerify[],
      AnalyticsMeteringPointParams
    >({
      query: params => ({
        url: `/metering-points-to-verify/query`,
        params,
      }),
      providesTags: ['MeteringPointsToVerify'],
      keepUnusedDataFor: 0,
    }),
    getMeteringPointsToVerifyI: builder.infiniteQuery<
      MeteringPointToVerify[],
      AnalyticsMeteringPointParams,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: `/metering-points-to-verify/query`,
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: () => ['MeteringPointsToVerify'],
    }),
    getGasDistribution: builder.query<
      {
        items: [
          {
            organizationName: string;
            budgetExpense: number;
            industryExpense: number;
            religionExpense: number;
            thermalPowerEngineeringExpense: number;
            ownNeedsExpense: number;
            privateSectorAndVTVExpense: number;
          },
        ];
      },
      GasDistributionByCategoryChartFilters
    >({
      query: ({ formType, ...data }) => ({
        url: `/gas-distribution/${formType}/query`,
        method: 'POST',
        data,
      }),
      providesTags: ['GasDistribution'],
      keepUnusedDataFor: 0,
    }),
    getCounterpartyContractAppendix2: builder.query<{ link: string }, void>({
      query: () => ({
        url: `/counterparty-contract-appendix2/export-key`,
        method: 'GET',
        transformResponse: transformAppendix2ExportKeyResponse,
      }),
      providesTags: ['CounterpartyContractAppendix2'],
      keepUnusedDataFor: 0,
    }),
    getGenericModel: builder.query<GenericReportModelResponse, void>({
      query: () => ({
        url: `/generic/model`,
        method: 'GET',
      }),
      providesTags: ['GenericModel'],
      keepUnusedDataFor: 0,
    }),
    getGenericEntity: builder.query<
      GenericReportEntityResponse,
      GenericEntityRequest
    >({
      query: ({ entityName }) => ({
        url: `/generic/entity`,
        method: 'GET',
        params: { entityName },
      }),
      providesTags: ['GenericEntity'],
      keepUnusedDataFor: 0,
    }),
    getGenericPreviewI: builder.infiniteQuery<
      MeteringPointToVerify[],
      GenericPreviewRequestParams,
      { page: { number: number; size: number } }
    >({
      infiniteQueryOptions: genericPaginationOptions,
      query: ({ queryArg, pageParam }) => ({
        url: `/generic/preview`,
        method: 'POST',
        data: { ...queryArg, ...pageParam },
      }),
      providesTags: () => ['GenericPreview'],
      keepUnusedDataFor: 0,
    }),
    export: builder.mutation<ArrayBuffer, ExportMutationParams>({
      query: data => ({
        url: `/generic/export`,
        method: 'POST',
        data: { ...data, export: true },
        responseType: 'arraybuffer',
      }),
    }),
    getAllowedReports: builder.infiniteQuery<
      AllowedReport[],
      FiltersGrid,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: `/allowed-reports/query`,
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: ['AllowedReports'],
      keepUnusedDataFor: 0,
    }),
  }),
});

export const {
  useExportOperationalExpensesMutation,
  useGetFormHistoryQuery,
  useLazyGetFormHistoryQuery,
  useGetPreviewQuery,
  useLazyGetPreviewQuery,
  useGenerateFormMutation,
  useRegenerateFormMutation,
  useRemoveFormMutation,
  useLazyExportFormQuery,
  useGetExpiredDevicesQuery,
  useGetMeteringPointsToDisconnectQuery,
  useGetMeteringPointsToVerifyQuery,
  useGetGasDistributionQuery,
  useGetCounterpartyContractAppendix2Query,
  useGetGenericModelQuery,
  useGetGenericEntityQuery,
  useExportMutation,
} = reportsApi;

export const useGetExpiredDevicesIInfiniteQuery = normalizeInfiniteQueryData(
  reportsApi.useGetExpiredDevicesIInfiniteQuery,
);

export const useGetFormHistoryIInfiniteQuery = normalizeInfiniteQueryData(
  reportsApi.useGetFormHistoryIInfiniteQuery,
);

export const useGetPreviewIInfiniteQuery = normalizeInfiniteQueryData(
  reportsApi.useGetPreviewIInfiniteQuery,
  'records',
);

export const useGetMeteringPointsToDisconnectIInfiniteQuery =
  normalizeInfiniteQueryData(
    reportsApi.useGetMeteringPointsToDisconnectIInfiniteQuery,
  );

export const useGetMeteringPointsToVerifyIInfiniteQuery =
  normalizeInfiniteQueryData(
    reportsApi.useGetMeteringPointsToVerifyIInfiniteQuery,
  );

export const addRowIdsAndDeduplicate = <T extends { rows: any[] }>(
  pages: T[],
) => {
  const byId = new Map<string, T>();

  pages.forEach(page => {
    page.rows.forEach((row, idx) => {
      const key = `null-${idx}-${Math.random()}`;
      byId.set(key, row);
    });
  });

  return Array.from(byId.values()).map((item, idx) => ({
    item,
    __rowId: idx + 1,
    __id: idx + 1,
  }));
};

const normalizeGenericPreviewInfiniteQueryData =
  <T extends { rows: any[] }>(hook: any) =>
  (params?: any, options?: any) => {
    const payload = hook(params, options);

    return {
      ...payload,
      data: {
        ...payload.data,
        rows: addRowIdsAndDeduplicate<T>(payload?.data?.pages ?? []),
      },
    };
  };

export const useGetGenericPreviewIInfiniteQuery =
  normalizeGenericPreviewInfiniteQueryData(
    reportsApi.useGetGenericPreviewIInfiniteQuery,
  );

export const useGetAllowedReportsInfiniteQuery = normalizeInfiniteQueryData(
  reportsApi.useGetAllowedReportsInfiniteQuery,
);
